import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component, ErrorInfo, ReactNode } from 'react';

interface WidgetErrorBoundaryProps {
  children: ReactNode;
  widgetId?: string;
  widgetTitle?: string;
  onRetry?: () => void;
  fallback?: ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Specialized Error Boundary for Dashboard Widgets
 *
 * Features:
 * - Compact error display suitable for widget containers
 * - Retry functionality with exponential backoff
 * - Widget-specific error logging
 * - Graceful degradation without breaking the entire dashboard
 * - Brand-compliant styling
 */
class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
  private maxRetries = 3;
  private retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<WidgetErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { widgetId, widgetTitle } = this.props;

    // Log widget-specific error for debugging
    if (import.meta.env.DEV) {
      console.group(`Widget Error: ${widgetTitle || widgetId || 'Unknown Widget'}`);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    // In production, you could send this to an error monitoring service
    // ErrorBoundaryMonitor.logWidgetError(widgetId, error, errorInfo);
  }

  componentWillUnmount() {
    // Clean up any pending retry timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  handleRetry = () => {
    const { onRetry } = this.props;
    const { retryCount } = this.state;

    if (retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        retryCount: prevState.retryCount + 1,
      }));

      // Call custom retry handler if provided
      onRetry?.();
    }
  };

  handleAutoRetry = () => {
    const { retryCount } = this.state;

    if (retryCount < this.maxRetries) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;

      const timeout = setTimeout(() => {
        this.handleRetry();
      }, delay);

      this.retryTimeouts.push(timeout);
    }
  };

  render() {
    const { children, fallback, widgetTitle } = this.props;
    const { hasError, error, retryCount } = this.state;

    if (hasError) {
      // Custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default compact error UI for widgets
      return (
        <div className="h-full flex flex-col items-center justify-center p-4 text-center bg-gray-50 rounded-lg">
          {/* Error Icon */}
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>

          {/* Error Title */}
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            {widgetTitle ? `${widgetTitle} Error` : 'Widget Error'}
          </h4>

          {/* Error Message */}
          <p className="text-xs text-gray-500 mb-3 max-w-[200px]">
            {error?.message?.substring(0, 100) || 'Failed to load widget data'}
          </p>

          {/* Retry Button */}
          <button
            onClick={this.handleRetry}
            disabled={retryCount >= this.maxRetries}
            className={`
              inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md
              transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
              ${
                retryCount >= this.maxRetries
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }
            `}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {retryCount >= this.maxRetries
              ? 'Max retries reached'
              : `Retry (${this.maxRetries - retryCount} left)`}
          </button>

          {/* Development Error Details */}
          {import.meta.env.DEV && error && (
            <details className="mt-3 w-full text-left">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                Error Details
              </summary>
              <pre className="mt-1 p-2 bg-white rounded border border-gray-200 text-xs text-red-600 overflow-auto max-h-24 whitespace-pre-wrap">
                {error.stack || error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return children;
  }
}

/**
 * Higher-order component to wrap widgets with error boundary
 */
export function withWidgetErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  widgetTitle?: string
) {
  return function WithErrorBoundary(props: P) {
    return (
      <WidgetErrorBoundary widgetTitle={widgetTitle}>
        <WrappedComponent {...props} />
      </WidgetErrorBoundary>
    );
  };
}

/**
 * Hook-friendly wrapper component for functional components
 */
export function WidgetErrorFallback({
  error,
  onRetry,
  widgetTitle,
}: {
  error: Error | null;
  onRetry?: () => void;
  widgetTitle?: string;
}) {
  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-center bg-gray-50 rounded-lg">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
        <AlertTriangle className="h-6 w-6 text-red-600" />
      </div>

      <h4 className="text-sm font-semibold text-gray-900 mb-1">
        {widgetTitle ? `${widgetTitle} Error` : 'Widget Error'}
      </h4>

      <p className="text-xs text-gray-500 mb-3 max-w-[200px]">
        {error?.message?.substring(0, 100) || 'Failed to load widget data'}
      </p>

      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Loading skeleton for widgets
 */
export function WidgetLoadingSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="h-full p-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 bg-gray-200 rounded mb-2"
          style={{ width: `${Math.random() * 40 + 60}%` }}
        />
      ))}
      <div className="mt-4 flex space-x-2">
        <div className="h-8 w-8 bg-gray-200 rounded" />
        <div className="h-8 w-8 bg-gray-200 rounded" />
        <div className="h-8 w-8 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

/**
 * Empty state for widgets with no data
 */
export function WidgetEmptyState({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const IconComponent = Icon || AlertTriangle;

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-center">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <IconComponent className="h-6 w-6 text-gray-400" />
      </div>
      <h4 className="text-sm font-medium text-gray-900 mb-1">{title}</h4>
      {description && <p className="text-xs text-gray-500 max-w-[200px]">{description}</p>}
    </div>
  );
}

export default WidgetErrorBoundary;
